// @ts-strict-ignore
import { useMutation } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { Select } from "@dashboard/components/Select";
import useNotifier from "@dashboard/hooks/useNotifier";
import { TextField } from "@material-ui/core";
import { Box } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import { CAROUSEL_BANNER_CREATE_MUTATION, CAROUSEL_BANNER_UPDATE_MUTATION } from "../cmsMutations";
import { CAROUSEL_BANNERS_QUERY } from "../cmsQueries";
import { CarouselBanner, CMSDeviceType, CMSPlatform } from "../types";
import CmsToggle from "./CmsToggle";

interface BannerDialogProps {
  open: boolean;
  banner?: CarouselBanner | null;
  onClose: () => void;
}

const REDIRECT_OPTIONS = [
  { label: "None", value: "NONE" },
  { label: "Product", value: "PRODUCT" },
  { label: "Category", value: "CATEGORY" },
  { label: "Collection", value: "COLLECTION" },
  { label: "External URL", value: "URL" },
];

const PLATFORM_OPTIONS = [
  { label: "All Platforms (App & Web)", value: "ALL" },
  { label: "Mobile App Only", value: "APP" },
  { label: "Web App Only", value: "WEB" },
];

const DEVICE_OPTIONS = [
  { label: "All Devices", value: "ALL" },
  { label: "Mobile (Smartphones)", value: "MOBILE" },
  { label: "Tablet", value: "TABLET" },
  { label: "Desktop / Laptop", value: "DESKTOP" },
];

export const BannerDialog: React.FC<BannerDialogProps> = ({ open, banner, onClose }) => {
  const notify = useNotifier();
  const isEdit = !!banner;

  const [title, setTitle] = useState(banner?.title || "");
  const [subtitle, setSubtitle] = useState(banner?.subtitle || "");
  const [description, setDescription] = useState(banner?.description || "");
  const [redirectType, setRedirectType] = useState(banner?.redirectType || "NONE");
  const [redirectValue, setRedirectValue] = useState(banner?.redirectValue || "");
  const [targetPlatform, setTargetPlatform] = useState<CMSPlatform>(banner?.targetPlatform || "ALL");
  const [targetDevice, setTargetDevice] = useState<CMSDeviceType>(banner?.targetDevice || "ALL");
  const [isPublished, setIsPublished] = useState(banner ? banner.isPublished : true);
  const [imageFile, setImageFile] = useState<File | null>(null);

  React.useEffect(() => {
    if (banner) {
      setTitle(banner.title || "");
      setSubtitle(banner.subtitle || "");
      setDescription(banner.description || "");
      setRedirectType(banner.redirectType || "NONE");
      setRedirectValue(banner.redirectValue || "");
      setTargetPlatform(banner.targetPlatform || "ALL");
      setTargetDevice(banner.targetDevice || "ALL");
      setIsPublished(banner.isPublished ?? true);
      setImageFile(null);
    } else {
      setTitle("");
      setSubtitle("");
      setDescription("");
      setRedirectType("NONE");
      setRedirectValue("");
      setTargetPlatform("ALL");
      setTargetDevice("ALL");
      setIsPublished(true);
      setImageFile(null);
    }
  }, [banner, open]);

  const [createBanner, { loading: creating }] = useMutation(CAROUSEL_BANNER_CREATE_MUTATION, {
    refetchQueries: [{ query: CAROUSEL_BANNERS_QUERY }],
    onCompleted: data => {
      if (data.carouselBannerCreate?.errors?.length) {
        notify({ status: "error", text: data.carouselBannerCreate.errors[0].message });
      } else {
        notify({ status: "success", text: "Banner created successfully" });
        onClose();
      }
    },
  });

  const [updateBanner, { loading: updating }] = useMutation(CAROUSEL_BANNER_UPDATE_MUTATION, {
    refetchQueries: [{ query: CAROUSEL_BANNERS_QUERY }],
    onCompleted: data => {
      if (data.carouselBannerUpdate?.errors?.length) {
        notify({ status: "error", text: data.carouselBannerUpdate.errors[0].message });
      } else {
        notify({ status: "success", text: "Banner updated successfully" });
        onClose();
      }
    },
  });

  const handleSubmit = () => {
    if (!title) {
      notify({ status: "error", text: "Title is required" });
      return;
    }

    const input: any = {
      title,
      subtitle,
      description,
      redirectType,
      redirectValue,
      targetPlatform,
      targetDevice,
      isPublished,
    };
    if (imageFile) {
      input.image = imageFile;
    }

    if (isEdit) {
      updateBanner({ variables: { id: banner.id, input } });
    } else {
      createBanner({ variables: { input } });
    }
  };

  return (
    <ActionDialog
      open={open}
      size="md"
      title={isEdit ? "Edit Carousel Banner" : "Create Carousel Banner"}
      confirmButtonState={creating || updating ? "loading" : "default"}
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <Box display="flex" flexDirection="column" gap={4} width="100%">
        <TextField
          label="Title"
          fullWidth
          value={title}
          onChange={e => setTitle(e.target.value)}
          required
        />
        <TextField
          label="Subtitle"
          fullWidth
          value={subtitle}
          onChange={e => setSubtitle(e.target.value)}
        />
        <TextField
          label="Description"
          fullWidth
          multiline
          rows={2}
          value={description}
          onChange={e => setDescription(e.target.value)}
        />

        <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={3}>
          <Select
            label="Target Platform"
            name="targetPlatform"
            value={targetPlatform}
            options={PLATFORM_OPTIONS}
            onChange={({ target }) => setTargetPlatform(target.value as any)}
          />
          <Select
            label="Target Device"
            name="targetDevice"
            value={targetDevice}
            options={DEVICE_OPTIONS}
            onChange={({ target }) => setTargetDevice(target.value as any)}
          />
        </Box>

        <Select
          label="Redirect Type"
          name="redirectType"
          value={redirectType}
          options={REDIRECT_OPTIONS}
          onChange={({ target }) => setRedirectType(target.value as any)}
        />
        {redirectType !== "NONE" && (
          <TextField
            label="Redirect Value (Product ID / Category ID / URL)"
            fullWidth
            value={redirectValue}
            onChange={e => setRedirectValue(e.target.value)}
          />
        )}
        <Box display="flex" flexDirection="column" gap={1}>
          <label style={{ fontSize: "0.85rem", color: "#666" }}>
            Banner Image {banner?.imageUrl && "(Upload new to replace)"}
          </label>
          {banner?.imageUrl && (
            <Box marginBottom={1}>
              <img
                src={banner.imageUrl}
                alt={banner.title}
                style={{ maxHeight: 100, maxWidth: 200, objectFit: "contain", borderRadius: 4, border: "1px solid #e2e8f0" }}
              />
            </Box>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={e => setImageFile(e.target.files?.[0] || null)}
          />
        </Box>
        <CmsToggle
          label="Published & Active on Storefront"
          description="Make this carousel banner visible immediately to customers"
          checked={isPublished}
          onChange={setIsPublished}
        />
      </Box>
    </ActionDialog>
  );
};

export default BannerDialog;
